import { parseLocale } from '@lx/shared/utils';

/**
 * Saves the locale to sessionStorage for persistence across page reloads.
 * @param locale - The locale code to store
 * @param localeStorageKey - The storage key used for sessionStorage
 */
export function saveLocaleToStorage(locale: string, localeStorageKey: string): void {
  try {
    if (!localeStorageKey) {
      console.warn('saveLocaleToStorage: localeStorageKey is required');
      return;
    }
    sessionStorage.setItem(localeStorageKey, JSON.stringify(locale));
  } catch (error) {
    console.warn('Failed to save locale to sessionStorage', error);
  }
}

/**
 * Resolves the application locale based on priority:
 * 1. URL parameter (highest priority)
 * 2. SessionStorage (persisted from previous page load)
 * 3. Default locale (fallback)
 *
 * @param queryParameter - The query parameter name to look for in the URL
 * @param defaultLocale - The fallback locale from environment configuration
 * @param localeStorageKey - The storage key used for sessionStorage
 * @returns The resolved locale code
 */
export function resolveLocale(queryParameter: string, defaultLocale: string, localeStorageKey: string): string {
  const queryParamsMap = new URLSearchParams(document.location.search);
  const localeParam = queryParameter ? queryParamsMap.get(queryParameter) : null;

  const storedLocale = localeStorageKey ? getLocaleFromStorage(localeStorageKey) : null;
  return parseLocale(localeParam ?? storedLocale ?? defaultLocale);
}

/**
 * Retrieves the locale from sessionStorage.
 * @param localeStorageKey - The storage key used for sessionStorage
 * @returns The stored locale or null if not found or invalid
 */
function getLocaleFromStorage(localeStorageKey: string): string | null {
  try {
    const rawStoredLocale = sessionStorage.getItem(localeStorageKey);
    return rawStoredLocale && typeof rawStoredLocale === 'string' ? JSON.parse(rawStoredLocale) : null;
  } catch (error) {
    console.warn('Failed to retrieve locale from sessionStorage', error);
    return null;
  }
}
